import CssSphere from 'components/ui/CssSphere';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { getStatusColor } from 'components/Orders/OrderStatus';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReturnOnOrderLine } from 'returnCenter-api';
import { styled } from 'styled-components';
import { getStatusTypeString } from 'utils/helpers';

type ReturnsOnThisLineCellProps = {
    returns?: ReturnOnOrderLine[] | null;
    origin: string | null;
};

const ReturnsOnThisLineCell = ({ returns, origin }: ReturnsOnThisLineCellProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const entries = useMemo(() => returns?.filter(Boolean) ?? [], [returns]);

    const handleNavigate = (returnId?: ReturnOnOrderLine['returnId'], status?: ReturnOnOrderLine['status']) => {
        if (!returnId) return;
        const statusKey = getStatusTypeString(status);
        if (!statusKey) return;

        navigate(`/returns/${statusKey}/${returnId}`, {
            state: {
                origin: origin ?? statusKey,
                returnIdFromPath: returnId,
                returnStatus: status,
            },
        });
    };

    if (!entries.length) {
        return null;
    }

    return (
        <CellWrapper column gap="0.25rem" className="">
            {entries.map((returnInfo, idx) => (
                <EntryWrapper
                    key={`${returnInfo.returnId ?? 'return'}-${idx}`}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleNavigate(returnInfo.returnId, returnInfo.status);
                    }}
                >
                    <CssSphere size={8} color={getStatusColor(returnInfo.status ?? null)} />
                    <ReturnId as="span" bold fontSize={11}>
                        {`#${returnInfo.returnId ?? t('returnsOnThisLine.unknownId', 'N/A')}`}
                    </ReturnId>
                    <Quantity as="span" fontSize={11}>
                        ({returnInfo.totalReturnItems ?? 0})
                    </Quantity>
                </EntryWrapper>
            ))}
        </CellWrapper>
    );
};

export default ReturnsOnThisLineCell;

const CellWrapper = styled(Flex)``;

const EntryWrapper = styled(Flex).attrs({ gap: '0.35rem' })`
    padding: 0.25rem 0.5rem;
    border-radius: var(--radius);
    background-color: ${({ theme }) => theme.colors.fillLight};
    align-items: center;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.1s ease;

    &:hover {
        background-color: ${({ theme }) => theme.colors.fillHover};
        transform: translateX(2px);
    }
`;

const ReturnId = styled(Text)`
    color: ${({ theme }) => theme.colors.link};
`;

const Quantity = styled(Text)`
    color: ${({ theme }) => theme.colors.textFaded};
`;
